<template>
  <div>
    <v-card 
      :variant="grouped ? 'flat' : 'flat'" 
      class="post-card" 
      :class="{ 
        'standard-post-margin': !grouped,
        'grouped-post': grouped
      }"
    >
      <div v-if="post.unavailable" class="pa-6 text-center">
        <v-icon icon="mdi-alert-circle-outline" color="grey" size="large" class="mb-2"></v-icon>
        <div class="text-h6 text-grey-darken-1">This post isn't available anymore</div>
        <div class="text-caption text-grey">It may have been deleted by the author.</div>
        
        <div class="mt-4 d-flex justify-center">
          <v-btn
            v-if="showRemoveBookmark"
            prepend-icon="mdi-bookmark-remove-outline"
            color="error"
            variant="tonal"
            size="small"
            class="rounded-lg"
            @click.stop="$emit('remove-bookmark', post.uri)"
          >
            Remove from Bookmark
          </v-btn>
        </div>
      </div>

      <div 
        v-else 
        class="post-layout clickable-layout"
        @click="handlePostClick"
      >
        <!-- Left Column: Gutter with Avatar -->
        <div class="post-gutter">
          <v-avatar size="42" @click.stop="router.push(`/profile/${post.author.handle}`)" class="cursor-pointer mb-2">
            <v-img :src="post.author.avatar" v-if="post.author.avatar"></v-img>
            <v-icon v-else icon="mdi-account"></v-icon>
          </v-avatar>
        </div>

        <!-- Right Column: All Post Content -->
        <div class="post-content">
          <!-- Author Info -->
          <div class="d-flex align-center flex-nowrap overflow-hidden mb-1">
            <span 
              class="text-body-2 text-white cursor-pointer profile-link text-truncate" 
              style="font-weight: 800 !important;"
              @click.stop="router.push(`/profile/${post.author.handle}`)"
            >
              {{ post.author.displayName || post.author.handle }}
            </span>
            &nbsp;
            <span 
              class="text-caption text-grey cursor-pointer profile-link flex-shrink-0"
              @click.stop="router.push(`/profile/${post.author.handle}`)"
            >
              @{{ post.author.handle }}
            </span>
            &nbsp;
            <span class="text-caption text-grey flex-shrink-0">·</span>
            &nbsp;
            <v-tooltip location="bottom" open-delay="500">
              <template v-slot:activator="{ props }">
                <span
                  v-bind="props"
                  class="text-caption flex-shrink-0 post-time"
                >
                  {{ formatDate(post.indexedAt) }}
                </span>
              </template>
              <template v-slot:default>
                <span class="post-time-tooltip">{{ new Date(post.indexedAt).toLocaleString() }}</span>
              </template>
            </v-tooltip>
          </div>

          <!-- Refined Reply Indicator -->
          <div v-if="reply" class="d-flex align-center mt-n1 mb-1 opacity-80">
            <v-icon icon="mdi-arrow-right-bottom" size="x-small" color="grey" class="mr-1"></v-icon>
            <span class="text-caption text-grey">Replied to {{ reply.parent.author.displayName || reply.parent.author.handle }}</span>
          </div>

          <!-- Note Title -->
          <div v-if="isNote" class="text-h6 font-weight-black text-white mb-2 leading-tight">
            <v-icon icon="mdi-book-open-page-variant" size="x-small" color="primary" class="mr-1 mt-n1"></v-icon>
            {{ post.record.title }}
          </div>

          <!-- Post/Note Text -->
          <div v-if="isNote" class="text-body-2 text-grey-lighten-1 mb-2 line-clamp-3">
            {{ noteSnippet }}
          </div>
          <div v-else class="text-body-1 mb-2" v-html="renderContent(post.record.text, post.record.facets)" @click="handleContentClick" style="white-space: pre-wrap;"></div>

          <!-- Media Embeds -->
          <div v-if="post.embed" class="mb-4 rounded-lg overflow-hidden border border-white-05">
            <!-- Image Grid -->
            <div v-if="getMediaType(post.embed) === 'images'" class="image-grid">
              <v-img
                v-for="(img, index) in getImages(post.embed)"
                :key="img.thumb"
                :src="isGif(img.thumb) ? (img.fullsize || getBetterImageUrl(img.thumb)) : getBetterImageUrl(img.thumb)"
                class="rounded cursor-pointer shadow-sm position-relative"
                max-height="400"
                cover
                @click.stop="openLightbox(Number(index))"
              >
                <div v-if="isGif(img.thumb)" class="gif-badge">GIF</div>
              </v-img>
            </div>

            <!-- Native Video -->
            <div v-else-if="getMediaType(post.embed) === 'video'" class="video-container" style="position: relative;">
              <video
                ref="videoRef"
                :controls="!isGifPresentation"
                :autoplay="!isGifPresentation"
                loop
                muted
                playsinline
                :preload="isGifPresentation ? 'none' : 'auto'"
                :poster="post.embed.thumbnail || post.embed.media?.thumbnail"
                class="w-100 d-block"
                style="max-height: 500px; background: black;"
              >
                <source :src="post.embed.playlist || post.embed.media?.playlist" type="application/x-mpegURL">
                Your browser does not support the video tag.
              </video>
              <!-- GIF play button overlay -->
              <div
                v-if="isGifPresentation && !gifPlaying"
                style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; cursor: pointer;"
                @click.stop="handleGifPlay"
              >
                <div style="width: 56px; height: 56px; background: rgba(0,0,0,0.65); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                  <v-icon color="white" size="32">mdi-play</v-icon>
                </div>
              </div>
              <!-- GIF badge -->
              <div v-if="isGifPresentation" class="gif-badge">GIF</div>
              <!-- Processing overlay shown while video.bsky.app is transcoding -->
              <div
                v-if="videoProcessing"
                style="position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; background: rgba(0,0,0,0.6); border-radius: 8px; gap: 12px;"
              >
                <v-progress-circular indeterminate color="white" size="40"></v-progress-circular>
                <span style="color: white; font-size: 13px;">Processing video...</span>
              </div>
            </div>

            <!-- External Link (Can be GIF Embed or Card) -->
            <template v-else-if="getMediaType(post.embed) === 'external'">
              <!-- GIF Embed Style (No Metadata) -->
              <v-card 
                v-if="isGif('', getExternal(post.embed))"
                variant="flat" 
                class="gif-embed-card border border-white-05 bg-black overflow-hidden" 
                :href="getExternal(post.embed).uri" 
                target="_blank"
                @click.stop
              >
                <v-img
                  v-if="getExternal(post.embed).thumb || getExternal(post.embed).uri"
                  :src="getBetterImageUrl(getExternal(post.embed).thumb, getExternal(post.embed).uri)"
                  max-height="600"
                  min-height="200"
                  class="position-relative bg-black"
                >
                  <div class="gif-badge">GIF</div>
                </v-img>
              </v-card>

              <!-- Standard External Link Card -->
              <v-card 
                v-else
                variant="flat" 
                class="external-card bg-black-05" 
                :href="getExternal(post.embed).uri" 
                target="_blank"
                @click.stop
              >
                <v-img
                  v-if="getExternal(post.embed).thumb"
                  :src="getExternal(post.embed).thumb"
                  height="200"
                  cover
                ></v-img>
                <v-card-item>
                  <v-card-subtitle class="text-uppercase text-caption font-weight-bold text-primary mb-1">
                    {{ getDomain(getExternal(post.embed).uri) }}
                  </v-card-subtitle>
                  <v-card-title class="text-subtitle-1 font-weight-bold pb-1">
                    {{ getExternal(post.embed).title }}
                  </v-card-title>
                  <v-card-text class="text-caption text-grey pa-0 line-clamp-2">
                    {{ getExternal(post.embed).description }}
                  </v-card-text>
                </v-card-item>
              </v-card>
            </template>
          </div>

          <!-- Quoted Record (Quote Post) -->
          <div v-if="getQuotedRecord(post.embed) && !isSpecialBlueskyRecord(getQuotedRecord(post.embed))" class="mb-4">
            <QuotedPost :record="getQuotedRecord(post.embed)" />
          </div>

          <!-- Linked Notes Previews -->
          <div v-if="noteLinks.length > 0 && !hideNoteEmbed" class="mb-2">
            <NoteEmbed 
              v-for="link in noteLinks" 
              :key="link.rkey" 
              :handle="link.handle" 
              :rkey="link.rkey" 
            />
          </div>

          <!-- Bluesky Special Links (Feeds, Lists, Starter Packs) -->
          <div v-if="allBlueskyEmbeds.length > 0" class="mb-2">
            <BlueskyEmbed
              v-for="url in allBlueskyEmbeds"
              :key="url"
              :url="url"
            />
          </div>

          <!-- Instagram Embeds -->
          <div v-if="instagramLinks.length > 0" class="mb-2">
            <InstagramEmbed
              v-for="url in instagramLinks"
              :key="url"
              :url="url"
            />
          </div>

          <!-- Actions -->
          <div class="post-actions d-flex align-center mt-2">
            <template v-if="!isNote">
              <v-btn 
                prepend-icon="mdi-comment-outline" 
                variant="text" 
                size="small" 
                @click.stop="$emit('reply', post)"
                class="action-btn mr-4"
              >
                {{ post.replyCount || 0 }}
              </v-btn>
              
              <v-menu location="bottom start">
                <template v-slot:activator="{ props: menuProps }">
                  <v-btn 
                    v-bind="menuProps"
                    :prepend-icon="isReposted ? 'mdi-repeat-variant' : 'mdi-repeat'" 
                    :color="isReposted ? 'success' : undefined"
                    variant="text" 
                    size="small" 
                    :loading="reposting"
                    class="action-btn mr-4"
                  >
                    {{ repostCount }}
                  </v-btn>
                </template>
                <v-list class="bg-surface rounded-lg border border-white-05 py-0">
                  <v-list-item @click.stop="handleRepost" class="py-2">
                    <template v-slot:prepend>
                      <v-icon :icon="isReposted ? 'mdi-repeat-variant' : 'mdi-repeat'" :color="isReposted ? 'success' : ''"></v-icon>
                    </template>
                    <v-list-item-title :class="{ 'text-success': isReposted }">
                      {{ isReposted ? 'Undo repost' : 'Repost' }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-divider class="border-white-05"></v-divider>
                  <v-list-item @click.stop="$emit('quote', post)" class="py-2">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-format-quote-open"></v-icon>
                    </template>
                    <v-list-item-title>Quote Post</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-btn 
                :prepend-icon="isLiked ? 'mdi-heart' : 'mdi-heart-outline'" 
                :color="isLiked ? 'error' : undefined"
                variant="text" 
                size="small" 
                @click.stop="handleLike"
                :loading="liking"
                class="action-btn mr-4"
              >
                {{ likeCount }}
              </v-btn>
            </template>

            <v-spacer></v-spacer>
            
            <v-btn
              v-if="user"
              :icon="isBookmarked ? 'mdi-bookmark' : 'mdi-bookmark-outline'"
              :color="isBookmarked ? 'primary' : undefined"
              variant="text"
              size="small"
              class="mr-1"
              @click.stop="showBookmarkDialog = true"
            ></v-btn>

            <v-btn
              icon
              variant="text"
              size="small"
              @click.stop
            >
              <v-icon icon="mdi-dots-vertical"></v-icon>
              <v-menu activator="parent" location="bottom end" transition="slide-y-transition">
                <v-list density="compact" class="rounded-lg shadow-lg border border-white-05" @click.stop>
                  <v-list-item
                    v-if="showRemoveBookmark"
                    prepend-icon="mdi-bookmark-remove-outline"
                    title="Remove from Group"
                    @click="$emit('remove-bookmark', post.uri)"
                  ></v-list-item>
                  <v-list-item
                    prepend-icon="mdi-open-in-new"
                    title="View Original"
                    @click="handleViewOriginal"
                  ></v-list-item>
                  <v-list-item
                    prepend-icon="mdi-link-variant"
                    title="Copy Link"
                    @click="copyPostUrl"
                  ></v-list-item>
                  <v-divider class="my-1 border-white-05"></v-divider>
                  <v-list-item
                    prepend-icon="mdi-translate"
                    title="Translate"
                    @click="translatePost"
                  ></v-list-item>
                  <v-list-item
                    prepend-icon="mdi-content-copy"
                    title="Copy post text"
                    @click="copyPostText"
                  ></v-list-item>
                  <v-divider v-if="isAuthor" class="my-1 border-white-05"></v-divider>
                  <v-list-item
                    v-if="isAuthor"
                    prepend-icon="mdi-delete-outline"
                    title="Delete Post"
                    base-color="error"
                    @click.prevent.stop="handleDelete"
                  ></v-list-item>
                </v-list>
              </v-menu>
            </v-btn>
          </div>
        </div>
      </div>
      
      <!-- Slot for grouped replies -->
      <v-divider v-if="$slots.default" class="border-white-05"></v-divider>
      <slot></slot>
    </v-card>

      <AddToBookmarkDialog
        v-model="showBookmarkDialog"
        :post-uri="post.uri"
      />

      <!-- Image Lightbox -->
      <v-dialog
        v-model="lightboxVisible"
        fullscreen
        transition="fade-transition"
        class="lightbox-dialog"
        @keydown.esc="lightboxVisible = false"
        @keydown.left="prevImage"
        @keydown.right="nextImage"
      >
        <div class="lightbox-content py-4 px-4 h-100 d-flex flex-column">
          <div class="d-flex align-center justify-end mb-2">
            <span class="text-white mr-4 text-caption font-weight-bold" v-if="getImages(post.embed).length > 1">
              {{ currentImageIndex + 1 }} / {{ getImages(post.embed).length }}
            </span>
            <v-btn
              icon="mdi-close"
              variant="text"
              color="white"
              @click="lightboxVisible = false"
              class="close-btn"
            ></v-btn>
          </div>

          <div class="flex-grow-1 d-flex align-center justify-center position-relative">
            <v-btn
              v-if="getImages(post.embed).length > 1"
              icon="mdi-chevron-left"
              variant="text"
              color="white"
              size="x-large"
              class="nav-btn prev-btn"
              @click.stop="prevImage"
            ></v-btn>

            <div class="image-container d-flex align-center justify-center">
              <v-img
                :src="getImages(post.embed)[currentImageIndex]?.fullsize || getImages(post.embed)[currentImageIndex]?.thumb"
                max-width="90vw"
                max-height="80vh"
                contain
              >
                <template v-slot:placeholder>
                  <div class="d-flex align-center justify-center fill-height">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  </div>
                </template>
              </v-img>
            </div>

            <v-btn
              v-if="getImages(post.embed).length > 1"
              icon="mdi-chevron-right"
              variant="text"
              color="white"
              size="x-large"
              class="nav-btn next-btn"
              @click.stop="nextImage"
            ></v-btn>
          </div>

          <div v-if="getImages(post.embed)[currentImageIndex]?.alt" class="lightbox-caption pa-4 text-center">
            <p class="text-white text-body-2">{{ getImages(post.embed)[currentImageIndex].alt }}</p>
          </div>
        </div>
      </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { currentUser, bookmarkedUris, likePost, unlikePost, repostPost, unrepostPost, showAlert, showConfirm } from '@/services/bluesky';
import { renderRichText, handleRichTextClick } from '@/utils/richtext';
import AddToBookmarkDialog from './AddToBookmarkDialog.vue';
import QuotedPost from './QuotedPost.vue';
import NoteEmbed, { extractNoteLinks } from './NoteEmbed.vue';
import BlueskyEmbed, { extractBlueskyLinks } from './BlueskyEmbed.vue';
import InstagramEmbed, { extractInstagramLinks } from './InstagramEmbed.vue';

const router = useRouter();

const props = defineProps({
  post: {
    type: Object,
    required: true
  },
  reply: {
    type: Object,
    default: null
  },
  isReply: {
    type: Boolean,
    default: false
  },
  showReplyLine: {
    type: Boolean,
    default: false
  },
  showRemoveBookmark: {
    type: Boolean,
    default: false
  },
  grouped: {
    type: Boolean,
    default: false
  },
  hideDivider: {
    type: Boolean,
    default: false
  },
  hideNoteEmbed: {
    type: Boolean,
    default: false
  },
  videoProcessing: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['view-thread', 'reply', 'delete', 'tag-click', 'remove-bookmark', 'view-profile', 'quote']);

const showBookmarkDialog = ref(false);
const user = currentUser;

// Lightbox state
const lightboxVisible = ref(false);
const currentImageIndex = ref(0);
const videoRef = ref<HTMLVideoElement | null>(null);
let hlsInstance: any = null;

const isGifPresentation = computed(() => {
  const embed = props.post.embed;
  if (!embed) return false;
  if (embed.$type === 'app.bsky.embed.video#view') return embed.presentation === 'gif';
  if (embed.$type === 'app.bsky.embed.recordWithMedia#view') return embed.media?.presentation === 'gif';
  return false;
});
const gifPlaying = ref(false);

const handleGifPlay = () => {
  gifPlaying.value = true;
  const video = videoRef.value;
  if (video) video.play().catch(e => console.warn('GIF play failed:', e));
};

const getBlobFallbackUrl = (playlistUrl: string): string | null => {
  const match = playlistUrl.match(/\/watch\/([^/]+)\/([^/]+)\/playlist\.m3u8/);
  if (!match) return null;
  const did = match[1];
  const cid = match[2];
  return `https://bsky.social/xrpc/com.atproto.sync.getBlob?did=${encodeURIComponent(did)}&cid=${encodeURIComponent(cid)}`;
};

const initHls = () => {
  const video = videoRef.value;
  if (!video) return;

  const src = props.post.embed?.playlist || props.post.embed?.media?.playlist;
  if (!src || !src.endsWith('.m3u8')) return;

  const run = () => {
    // If component was unmounted during wait
    if (!videoRef.value) return;

    // Use native HLS if supported (Safari)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
    }
    // Else use Hls.js if available
    else if ((window as any).Hls) {
      const Hls = (window as any).Hls;
      if (Hls.isSupported()) {
        if (hlsInstance) hlsInstance.destroy();
        hlsInstance = new Hls();
        hlsInstance.loadSource(src);
        hlsInstance.attachMedia(video);
        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
          video.muted = true;
          if (!isGifPresentation.value) {
            video.play().catch(e => console.warn('Autoplay failed:', e));
          } else if (gifPlaying.value) {
            // HLS was re-initialized while already playing (e.g. silent thread reload) — resume
            video.play().catch(e => console.warn('GIF resume failed:', e));
          } else {
            // First load: wait 1 s then autoplay
            setTimeout(() => {
              gifPlaying.value = true;
              if (videoRef.value) videoRef.value.play().catch(e => console.warn('GIF autoplay failed:', e));
            }, 1000);
          }
        });
        hlsInstance.on(Hls.Events.ERROR, (_: any, data: any) => {
          if (data.fatal) {
            const fallback = getBlobFallbackUrl(src);
            if (fallback && video.src !== fallback) {
              console.warn('[HLS] Fatal error, trying PDS blob fallback:', fallback);
              hlsInstance.destroy();
              hlsInstance = null;
              video.src = fallback;
              video.load();
              video.play().catch(e => console.warn('Fallback autoplay failed:', e));
            }
          }
        });
      }
    } else {
      // Retry in 200ms
      setTimeout(run, 200);
    }
  };
  run();
};

onMounted(() => {
  // Wait for next tick to ensure videoRef is available
  nextTick(() => {
    initHls();
  });
});

onBeforeUnmount(() => {
  if (hlsInstance) {
    hlsInstance.destroy();
  }
});

watch(() => props.post.embed, () => {
  nextTick(() => {
    initHls();
  });
}, { deep: true });

watch(() => props.videoProcessing, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    nextTick(() => {
      const video = videoRef.value;
      if (video) {
        video.muted = true;
        video.play().catch(() => {});
      }
    });
  }
});

const openLightbox = (index: number) => {
  currentImageIndex.value = index;
  lightboxVisible.value = true;
};

const nextImage = () => {
  const imgs = getImages(props.post.embed);
  if (imgs.length <= 1) return;
  currentImageIndex.value = (currentImageIndex.value + 1) % imgs.length;
};

const prevImage = () => {
  const imgs = getImages(props.post.embed);
  if (imgs.length <= 1) return;
  currentImageIndex.value = (currentImageIndex.value - 1 + imgs.length) % imgs.length;
};

// Local interaction state
const isLiked = ref(!!props.post.viewer?.like);
const likeUri = ref(props.post.viewer?.like);
const likeCount = ref(props.post.likeCount || 0);
const liking = ref(false);

const isReposted = ref(!!props.post.viewer?.repost);
const repostUri = ref(props.post.viewer?.repost);
const repostCount = ref(props.post.repostCount || 0);
const reposting = ref(false);

const isBookmarked = computed(() => bookmarkedUris.value.has(props.post.uri));
const isAuthor = computed(() => user.value?.did === props.post.author.did);

const isNote = computed(() => {
  return props.post.$type === 'id.gamedev.note' || props.post.uri?.includes('id.gamedev.notes');
});

const noteSnippet = computed(() => {
  if (!isNote.value) return '';
  const firstTextBlock = props.post.record.blocks?.find((b: any) => b.type === 'text');
  if (!firstTextBlock) return '';
  
  const text = firstTextBlock.content
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/<[^>]*>/g, '')
    .substring(0, 200);
    
  return text + (firstTextBlock.content.length > 200 ? '...' : '');
});

const noteLinks = computed(() => {
  return extractNoteLinks(props.post.record.text || '');
});

const instagramLinks = computed(() => {
  return extractInstagramLinks(props.post.record.text || '');
});

const allBlueskyEmbeds = computed(() => {
  const links = extractBlueskyLinks(props.post.record.text || '');
  
  // Check if there's an official record embed
  const embed = props.post.embed;
  if (embed) {
    const record = getQuotedRecord(embed);
    if (record && isSpecialBlueskyRecord(record)) {
      const uri = record.uri;
      if (!links.includes(uri)) {
        links.push(uri);
      }
    }
  }
  
  return [...new Set(links)];
});

const isSpecialBlueskyRecord = (record: any) => {
  if (!record) return false;
  const type = record.$type;
  return type === 'app.bsky.feed.defs#generatorView' || 
         type === 'app.bsky.graph.defs#listView' || 
         type === 'app.bsky.graph.defs#starterPackView' ||
         record.uri?.includes('app.bsky.feed.generator') ||
         record.uri?.includes('app.bsky.graph.list') ||
         record.uri?.includes('app.bsky.graph.starterpack');
};

const handlePostClick = () => {
  const parts = props.post.uri.split('/');
  const rkey = parts.pop();
  const collection = parts[3];
  const handle = props.post.author.handle;
  if (collection === 'id.gamedev.notes') {
    router.push(`/profile/${handle}/note/${rkey}`);
  } else if (collection === 'app.bsky.feed.post') {
    router.push(`/profile/${handle}/post/${rkey}`);
  } else if (collection === 'app.bsky.feed.repost') {
    // If it's a repost, we should ideally go to the subject, but for now we'll just log
    console.warn('Navigating to a repost directly is not supported yet.');
  }
};

const handleViewOriginal = () => {
  const parts = props.post.uri.split('/');
  const rkey = parts.pop();
  const collection = parts[3];
  const handle = props.post.author.handle;
  if (collection === 'id.gamedev.notes') {
    router.push(`/profile/${handle}/note/${rkey}`);
  } else {
    window.open(`https://bsky.app/profile/${handle}/post/${rkey}`, '_blank');
  }
};

// Keep local state in sync with prop changes
watch(() => props.post, (newPost: any) => {
  isLiked.value = !!newPost.viewer?.like;
  likeUri.value = newPost.viewer?.like;
  likeCount.value = newPost.likeCount || 0;
  
  isReposted.value = !!newPost.viewer?.repost;
  repostUri.value = newPost.viewer?.repost;
  repostCount.value = newPost.repostCount || 0;
}, { deep: true });

const handleLike = async () => {
  if (!user.value) return;
  liking.value = true;
  try {
    if (isLiked.value) {
      await unlikePost(likeUri.value);
      isLiked.value = false;
      likeUri.value = null;
      likeCount.value--;
    } else {
      const res = await likePost(props.post.uri, props.post.cid);
      isLiked.value = true;
      likeUri.value = res.uri;
      likeCount.value++;
    }
  } catch (e) {
    console.error('Failed to toggle like:', e);
  } finally {
    liking.value = false;
  }
};

const handleRepost = async () => {
  if (!user.value) return;
  reposting.value = true;
  try {
    if (isReposted.value) {
      await unrepostPost(repostUri.value);
      isReposted.value = false;
      repostUri.value = null;
      repostCount.value--;
    } else {
      const res = await repostPost(props.post.uri, props.post.cid);
      isReposted.value = true;
      repostUri.value = res.uri;
      repostCount.value++;
    }
  } catch (e) {
    console.error('Failed to toggle repost:', e);
  } finally {
    reposting.value = false;
  }
};

const handleContentClick = (event: MouseEvent) => {
  handleRichTextClick(event, { 
    router, 
    onTagClick: (tag: string) => emit('tag-click', tag) 
  });
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 0) return 'now'; 
  if (diffInSeconds < 60) return `${diffInSeconds}s`;
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const copyPostUrl = () => {
  const handle = props.post.author.handle;
  const rkey = props.post.uri.split('/').pop();
  const url = `https://bsky.app/profile/${handle}/post/${rkey}`;
  
  navigator.clipboard.writeText(url).then(() => {
    // We could add a snackbar here later if needed
    console.log('URL copied to clipboard');
  }).catch(err => {
    console.error('Failed to copy: ', err);
  });
};

const copyPostText = () => {
  const text = props.post.record.text || '';
  navigator.clipboard.writeText(text).then(() => {
    console.log('Post text copied');
  });
};

const translatePost = () => {
  const text = encodeURIComponent(props.post.record.text || '');
  window.open(`https://translate.google.com/?sl=auto&tl=en&text=${text}&op=translate`, '_blank');
};

const handleDelete = () => {
  emit('delete', props.post);
};


const renderContent = (text: string, facets?: any[]) => renderRichText(text, facets);

const getMediaType = (embed: any) => {
  if (!embed) return null;
  const type = embed.$type;
  
  if (type === 'app.bsky.embed.images#view') return 'images';
  if (type === 'app.bsky.embed.video#view') return 'video';
  if (type === 'app.bsky.embed.external#view') return 'external';
  
  if (type === 'app.bsky.embed.recordWithMedia#view') {
    return getMediaType(embed.media);
  }
  
  return null;
};

const getQuotedRecord = (embed: any) => {
  if (!embed) return null;
  if (embed.$type === 'app.bsky.embed.record#view') return embed.record;
  if (embed.$type === 'app.bsky.embed.recordWithMedia#view') return embed.record.record;
  return null;
};

const getImages = (embed: any) => {
  if (embed.$type === 'app.bsky.embed.images#view') return embed.images;
  if (embed.$type === 'app.bsky.embed.recordWithMedia#view') return embed.media.images;
  return [];
};

const getExternal = (embed: any) => {
  if (embed.$type === 'app.bsky.embed.external#view') return embed.external;
  if (embed.$type === 'app.bsky.embed.recordWithMedia#view') return embed.media.external;
  return null;
};

const getDomain = (url: string) => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

const isGif = (url: string, external?: any) => {
  const isGifUrl = url?.toLowerCase().includes('gif') || url?.toLowerCase().endsWith('.gif');
  if (isGifUrl) return true;
  
  const thumb = external?.thumb || url;
  if (thumb?.toLowerCase().includes('media.tenor.com') || thumb?.toLowerCase().includes('giphy.com')) return true;

  if (external?.uri) {
    const uri = external.uri.toLowerCase();
    if (uri.includes('tenor.com') || uri.includes('giphy.com')) return true;
  }
  
  return false;
};

const getBetterImageUrl = (url: string, uri?: string) => {
  // If the URI itself is a direct GIF (common for Tenor), use it!
  if (uri?.toLowerCase().endsWith('.gif') || uri?.toLowerCase().includes('.gif?')) {
    return uri;
  }

  if (!url) return url;
  
  // Convert static Tenor thumbnails to GIFs
  if (url.includes('media.tenor.com')) {
    if (url.includes('/images/')) {
       return url.replace('/images/', '/').replace(/\.(png|jpe?g)$/, '.gif');
    }
    return url.replace(/\.(png|jpe?g)$/, '.gif');
  }
  
  if (url.includes('giphy.com')) {
    return url.replace(/\.(png|jpe?g)$/, '.gif');
  }
  return url;
};
</script>

<style>
.post-time-tooltip {
  color: #DBA858 !important;
  font-size: 0.8rem;
}

.v-tooltip > .v-overlay__content:has(.post-time-tooltip) {
  background: #1a1208 !important;
  border: 1px solid #DBA858 !important;
  color: #DBA858 !important;
  border-radius: 8px !important;
  padding: 4px 10px !important;
}
</style>

<style scoped>
.post-time {
  color: #a07840;
  transition: color 0.15s;
}
.post-time:hover {
  color: #DBA858;
}

.post-card {
  border-radius: 20px;
  background-color: #0d0d0d !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden !important;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.standard-post-margin {
  margin-bottom: 24px !important;
}

.clickable-layout {
  cursor: pointer;
}

/* Highlight specifically the hovered post content, not the entire card stack */
.post-layout {
  display: flex;
  padding: 20px 24px;
  position: relative;
  transition: background-color 0.2s ease;
}

.post-layout:hover {
  background-color: rgba(255, 255, 255, 0.02) !important;
}

.post-card:not(.grouped-post):hover {
  border-color: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
}

.post-gutter {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50px;
  flex-shrink: 0;
  position: relative;
  margin-right: 20px;
}

.post-content {
  flex: 1;
  min-width: 0;
}

.grouped-post {
  background: transparent !important;
  background-color: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  backdrop-filter: none !important;
  box-shadow: none !important;
  margin-bottom: 0 !important;
}

.grouped-post:hover {
  background-color: transparent !important;
  transform: none !important;
  box-shadow: none !important;
}

.grouped-post.hide-divider {
  border: none !important;
}

/* Ensure actions don't have built-in card padding */
.post-actions {
  margin-left: -8px;
}

/* Media Styles */
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
}

.video-container video {
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.external-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px !important;
  transition: background-color 0.2s;
  overflow: hidden;
}

.external-card:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.bg-black-05 {
  background-color: rgba(0, 0, 0, 0.2) !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.profile-link {
  text-decoration: none;
  transition: opacity 0.2s;
}

.gif-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 1;
}

.profile-link:hover {
  text-decoration: underline !important;
  opacity: 0.8;
}

:deep(.hashtag) {
  color: rgb(var(--v-theme-primary)) !important;
  text-decoration: none;
  font-weight: 500;
}

:deep(.hashtag:hover) {
  text-decoration: underline !important;
  opacity: 0.8;
}

:deep(.mention) {
  color: var(--v-theme-primary);
  text-decoration: none;
  font-weight: 500;
}

:deep(.mention:hover) {
  text-decoration: underline;
}

:deep(a) {
  color: #3b82f6;
  text-decoration: none;
}

:deep(a:hover) {
  text-decoration: underline;
}

/* Lightbox Styles */
.lightbox-dialog {
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px) saturate(180%);
}

.lightbox-content {
  position: relative;
  z-index: 2000;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(0, 0, 0, 0.3) !important;
  border-radius: 50% !important;
}

.nav-btn:hover {
  background: rgba(0, 0, 0, 0.6) !important;
}

.prev-btn {
  left: 20px;
}

.next-btn {
  right: 20px;
}

.image-container {
  width: 100%;
  height: 100%;
}

.lightbox-caption {
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  max-width: 600px;
  margin: 0 auto;
}
.display-name {
  font-weight: 900 !important;
}
</style>
